import {
  type Dispatch,
  FC,
  type SetStateAction,
  useCallback,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import ThreadedCommentsContent from './ThreadedCommentsContent';
import { type ThreadedComment } from './threadedComments';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  title: React.ReactNode;
  addCommentLabel?: React.ReactNode;
  canComment?: boolean;
  contentType: number;
  objectId: number;
  comments: ThreadedComment[];
  onCommentsChange: Dispatch<SetStateAction<ThreadedComment[]>>;
  onPublicCommentCountChange?: (count: number) => void;
}

const ThreadedCommentsModal: FC<Props> = ({
  isOpen,
  toggle,
  title,
  addCommentLabel,
  canComment,
  contentType,
  objectId,
  comments,
  onCommentsChange,
  onPublicCommentCountChange,
}) => {
  const { formatMessage } = useIntl();
  const [composerContent, setComposerContent] = useState('');

  const handleToggle = useCallback(() => {
    if (
      composerContent !== '' &&
      !window.confirm(
        formatMessage({
          id: 'app.views.widgets.comments.threaded_comments_modal.unsaved_changes_warning',
          defaultMessage:
            'You have an unsaved comment. Are you sure you want to close?',
        })
      )
    ) {
      return;
    }
    toggle();
  }, [toggle, formatMessage, composerContent]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      size="lg"
      contentClassName="h-100"
    >
      <ModalHeader
        toggle={handleToggle}
        cssModule={{ 'modal-title': 'text-truncate modal-title' }}
      >
        {title}
      </ModalHeader>
      <ModalBody
        className="d-flex flex-column"
        style={{ maxHeight: 'calc(100vh - 6rem)', overflowY: 'auto' }}
      >
        <ThreadedCommentsContent
          comments={comments}
          onCommentsChange={onCommentsChange}
          contentType={contentType}
          objectId={objectId}
          addCommentLabel={addCommentLabel}
          canComment={canComment}
          variant="modal"
          onComposerContentChange={setComposerContent}
          onPublicCommentCountChange={onPublicCommentCountChange}
        />
      </ModalBody>
    </Modal>
  );
};

export default ThreadedCommentsModal;
